import { useMemo, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const numCls = "h-11 w-full rounded-md border border-slate-300 bg-white px-2.5 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600";

function NumField({ label, value, onChange, min, max, step = "any", testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-500 mb-1">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className={numCls}
      />
    </label>
  );
}

export default function ApplianceStep({ library, items, setItems, showPeriod = false }) {
  const [search, setSearch] = useState("");
  const [customName, setCustomName] = useState("");

  const filtered = useMemo(
    () => library.filter((a) => a.name.toLowerCase().includes(search.toLowerCase())),
    [library, search]
  );

  const add = (a) => {
    if (items.some((i) => i.key === a.key)) return;
    setItems([...items, { key: a.key, name: a.name, watts: a.watts, hours: a.hours, surge: a.surge || 1, qty: 1, days_per_week: 7, essential: true }]);
  };

  const addCustom = () => {
    const name = customName.trim();
    if (!name) return;
    const key = "custom_" + name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40);
    if (items.some((i) => i.key === key)) return;
    setItems([...items, { key, name, watts: 100, hours: 2, surge: 1, qty: 1, days_per_week: 7, essential: true }]);
    setCustomName("");
  };

  const update = (key, patch) => setItems(items.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const remove = (key) => setItems(items.filter((i) => i.key !== key));

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
        <Input
          data-testid="appliance-search"
          placeholder="Search appliances — refrigerator, pump, TV…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-12 pl-10 bg-white border-slate-300 text-slate-900 focus-visible:ring-emerald-600"
        />
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 max-h-72 overflow-y-auto pr-1">
        {filtered.map((a) => {
          const added = items.some((i) => i.key === a.key);
          return (
            <button
              key={a.key}
              type="button"
              data-testid={`appliance-add-${a.key}`}
              onClick={() => add(a)}
              disabled={added}
              className={`rounded-md border p-3.5 text-left transition-colors min-h-[68px] ${
                added
                  ? "border-emerald-500 bg-emerald-50 cursor-default"
                  : "border-slate-200 bg-white hover:border-emerald-400"
              }`}
            >
              <span className="block text-sm font-semibold text-slate-900 leading-tight">{a.name}</span>
              <span className="mt-1 block text-[11px] text-slate-500">{a.watts} W · {a.hours} h/day{added ? " · Added" : ""}</span>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full py-6 text-center text-sm text-slate-500">
            No match — add it as a custom appliance below.
          </p>
        )}
      </div>

      <div className="mt-4 flex gap-2.5">
        <Input
          data-testid="custom-appliance-name"
          placeholder="Other appliance — type its name"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          className="h-12 bg-white border-slate-300 text-slate-900 focus-visible:ring-emerald-600"
        />
        <button
          type="button"
          onClick={addCustom}
          data-testid="custom-appliance-add"
          className="shrink-0 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add
        </button>
      </div>

      <div className="mt-7 space-y-4">
        {items.map((item) => (
          <div key={item.key} data-testid={`appliance-row-${item.key}`} className="rounded-lg border border-slate-200 bg-mist p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <p className="font-display text-sm font-bold text-slate-900">{item.name}</p>
              <button
                type="button"
                onClick={() => remove(item.key)}
                data-testid={`appliance-remove-${item.key}`}
                aria-label={`Remove ${item.name}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-500 hover:text-red-600 hover:border-red-400 transition-colors"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <NumField label="Quantity" min={1} max={100} step={1} value={item.qty} testid={`appliance-qty-${item.key}`} onChange={(v) => update(item.key, { qty: v })} />
              <NumField label="Power (W)" min={0.1} max={20000} value={item.watts} testid={`appliance-watts-${item.key}`} onChange={(v) => update(item.key, { watts: v })} />
              <NumField label="Hours / day" min={0} max={24} value={item.hours} testid={`appliance-hours-${item.key}`} onChange={(v) => update(item.key, { hours: v })} />
              <NumField label="Days / week" min={1} max={7} step={1} value={item.days_per_week} testid={`appliance-days-${item.key}`} onChange={(v) => update(item.key, { days_per_week: v })} />
              <NumField label="Start-up surge ×" min={1} max={6} value={item.surge} testid={`appliance-surge-${item.key}`} onChange={(v) => update(item.key, { surge: v })} />
              {showPeriod && (
                <label className="block">
                  <span className="block text-[11px] font-medium text-slate-500 mb-1">Mainly used</span>
                  <select
                    value={item.period || "both"}
                    data-testid={`appliance-period-${item.key}`}
                    onChange={(e) => update(item.key, { period: e.target.value })}
                    className={numCls}
                  >
                    <option value="both">Day & night</option>
                    <option value="day">Daytime</option>
                    <option value="night">Nighttime</option>
                  </select>
                </label>
              )}
            </div>
            <label className="mt-3 flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={item.essential}
                data-testid={`appliance-essential-${item.key}`}
                onChange={(e) => update(item.key, { essential: e.target.checked })}
                className="h-5 w-5 rounded border-slate-300 accent-emerald-600"
              />
              Essential — must stay on during a power cut
            </label>
          </div>
        ))}
        {items.length === 0 && (
          <p data-testid="appliance-empty" className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            Please add at least one appliance before continuing.
          </p>
        )}
      </div>
    </div>
  );
}
